/**
 * Dial geometry — build spec §10. Fully solved in v1; copied, not re-derived.
 */

export const CX = 200;
export const CY = 208;
export const R_OUT = 186;
export const R_IN = 96;
export const R_NEEDLE = 176;
/**
 * Padded beyond the face so the guess markers can sit OUTSIDE the arc, on the
 * empty page, where there is six times more arc length per scale unit than
 * inside the hub. Inside, five pills crowd into a huddle that reports nothing;
 * outside, they land under the value they belong to.
 */
export const VIEWBOX = '-28 -26 456 292';
/** Radius the guess pills sit at — clear of the face, inside the padded box. */
export const R_MARKER = 206;

/**
 * The dial FACE spans -5…105 (198°, wider than a half-turn) so the "0" and "10"
 * numerals have paper underneath them instead of straddling the edge onto the
 * dark page. The scale, ticks, numerals and bands span 0…100.
 */
export const FACE_MIN = -5;
export const FACE_MAX = 105;

/** Value 0–100 → a point at radius r. */
export function pt(r: number, v: number): [number, number] {
  const a = ((180 - v * 1.8) * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY - r * Math.sin(a)];
}

/**
 * An annular sector between two values.
 *
 * THE LARGE-ARC BUG — this one cost a full debugging cycle in v1. The SVG
 * large-arc-flag must be derived from the sweep angle. With it hard-coded to 0
 * the renderer draws the minor arc, and the whole dial renders as a squashed
 * lozenge with the target bands bursting outside the face. Do not "simplify"
 * this line.
 */
export function sector(v1: number, v2: number, rIn: number, rOut: number): string {
  const a = pt(rOut, v1);
  const b = pt(rOut, v2);
  const c = pt(rIn, v2);
  const d = pt(rIn, v1);
  const large = (v2 - v1) * 1.8 > 180 ? 1 : 0;
  const f = (n: number) => n.toFixed(2);
  return (
    `M${f(a[0])} ${f(a[1])}A${rOut} ${rOut} 0 ${large} 1 ${f(b[0])} ${f(b[1])}` +
    `L${f(c[0])} ${f(c[1])}A${rIn} ${rIn} 0 ${large} 0 ${f(d[0])} ${f(d[1])}Z`
  );
}

/**
 * Screen coordinates → dial value, via the SVG's own CTM. Never assume a
 * uniform scale factor: the dial is `width:100%` inside a flex column and the
 * scale differs per device.
 */
export function valueFromPoint(svg: SVGSVGElement, clientX: number, clientY: number): number | null {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const p = svg.createSVGPoint();
  p.x = clientX;
  p.y = clientY;
  const local = p.matrixTransform(ctm.inverse());
  const dx = local.x - CX;
  const dy = CY - local.y;
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  // Below the pivot: snap to whichever end the pointer is nearest.
  if (deg < 0) deg = dx > 0 ? 0 : 180;
  const v = (180 - deg) / 1.8;
  return v < 0 ? 0 : v > 100 ? 100 : v;
}

/**
 * Keyboard steps, on the internal 0–100 scale. Displayed, these are ±0.1,
 * ±0.5 and ±1.0 out of 10. Page keys exist because 100 arrow presses to cross
 * the dial is not operable (ARIA APG slider pattern).
 */
export const STEP_ARROW = 1;
export const STEP_SHIFT = 5;
export const STEP_PAGE = 10;
